import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import PageHero from '../components/Common/PageHero';
import MetricCard from '../components/Common/MetricCard';
import DataGrid from '../components/Common/DataGrid';

interface ServiceAccount {
  id: number;
  clientId: string;
  name: string;
  description?: string;
  isActive: boolean;
  maxRequestsPerWindow: number;
  allowedEndpoints?: string[];
  tokenExpiryMinutes: number;
  lastUsedAt?: string;
  totalRequests: number;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

interface ServiceAccountFormData {
  name: string;
  description: string;
  maxRequestsPerWindow: number;
  allowedEndpoints: string[];
}

interface NewServiceAccountResponse extends ServiceAccount {
  clientSecret: string;
  warning: string;
}

interface ScopeOption {
  value: string;
  label: string;
  description: string;
  group: string;
  recommended?: boolean;
}

const SERVICE_ACCOUNT_SCOPE_OPTIONS: ScopeOption[] = [
  {
    value: 'GET:/api/forms*',
    label: 'Read all forms',
    description: 'Allow read access to the wider forms endpoints for dashboards and sync tools.',
    group: 'Data access',
    recommended: true,
  },
  {
    value: 'GET:/api/forms/service-line/*',
    label: 'Lookup service lines',
    description: 'Fetch a single service line before running package or topup workflows.',
    group: 'Service-line operations',
    recommended: true,
  },
  {
    value: 'POST:/api/forms/service-line/*',
    label: 'Run topups and package updates',
    description: 'Allow operational POST actions under service-line routes such as topups and package changes.',
    group: 'Service-line operations',
    recommended: true,
  },
  {
    value: 'POST:/api/forms',
    label: 'Create a form',
    description: 'Allow the integration to create new service forms directly.',
    group: 'Form management',
  },
  {
    value: 'POST:/api/forms/bulk-import/preview',
    label: 'Preview bulk form import',
    description: 'Let the integration upload a spreadsheet and preview rows before committing them as forms.',
    group: 'Form management',
  },
  {
    value: 'POST:/api/forms/bulk-import/commit',
    label: 'Commit bulk form import',
    description: 'Allow the integration to commit a previewed bulk import, creating multiple forms at once.',
    group: 'Form management',
  },
  {
    value: 'GET:/api/notifications',
    label: 'Read notifications',
    description: 'Let the integration read notification summaries and operational alerts.',
    group: 'Operations',
  },
  {
    value: 'GET:/api/monitor/control/status',
    label: 'Read monitor status',
    description: 'Expose current monitor health and status to the external system.',
    group: 'Operations',
    recommended: true,
  },
  {
    value: 'POST:/api/usage-events*',
    label: 'Collect and acknowledge usage events',
    description: 'Let the integration collect queued usage events (50/75/100% thresholds, topups, reactivations) and acknowledge them so it can notify customers.',
    group: 'Usage events',
  },
  {
    value: 'GET:/api/usage-events*',
    label: 'Read usage event queue stats',
    description: 'Let the integration read how many usage events are pending, delivered or failed.',
    group: 'Usage events',
  },
];

const DEFAULT_SCOPE_SELECTION = SERVICE_ACCOUNT_SCOPE_OPTIONS
  .filter((option) => option.recommended)
  .map((option) => option.value);

const scopeMethodStyles: Record<string, string> = {
  GET: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200',
  POST: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200',
  PATCH: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200',
  PUT: 'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200',
  DELETE: 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200',
  '*': 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200',
};

const ServiceAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<ServiceAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<ServiceAccount | null>(null);
  const [search, setSearch] = useState('');
  const [newCredentials, setNewCredentials] = useState<{ clientId: string; clientSecret: string } | null>(null);
  const [formData, setFormData] = useState<ServiceAccountFormData>({
    name: '',
    description: '',
    maxRequestsPerWindow: 100,
    allowedEndpoints: DEFAULT_SCOPE_SELECTION
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/service-accounts?page=1&limit=100');
      // Handle paginated response
      setAccounts(response.data.data || []);
    } catch (error: any) {
      console.error('Error fetching service accounts:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch service accounts');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingAccount) {
        // Update existing account
        await api.patch(`/api/service-accounts/${editingAccount.id}`, {
          ...formData
        });
        toast.success('Service account updated successfully! 🔄');
        fetchAccounts();
        resetForm();
      } else {
        // Create new account
        const response = await api.post<NewServiceAccountResponse>('/api/service-accounts', {
          ...formData
        });
        toast.success('Service account created successfully! ✨');
        
        // Show credentials modal
        setNewCredentials({
          clientId: response.data.clientId,
          clientSecret: response.data.clientSecret
        });
        setShowCredentialsModal(true);
        
        fetchAccounts();
        resetForm();
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Operation failed';
      toast.error(message);
    }
  };

  const handleEdit = (account: ServiceAccount) => {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      description: account.description || '',
      maxRequestsPerWindow: account.maxRequestsPerWindow,
      allowedEndpoints: account.allowedEndpoints || []
    });
    setShowModal(true);
  };

  const toggleScope = (scope: string) => {
    setFormData((current) => ({
      ...current,
      allowedEndpoints: current.allowedEndpoints.includes(scope)
        ? current.allowedEndpoints.filter((entry) => entry !== scope)
        : [...current.allowedEndpoints, scope]
    }));
  };

  const handleToggleActive = async (accountId: number, isActive: boolean) => {
    try {
      await api.patch(`/api/service-accounts/${accountId}`, { isActive: !isActive });
      toast.success(`Service account ${!isActive ? 'enabled' : 'disabled'} successfully! 🔄`);
      fetchAccounts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update service account status');
    }
  };

  const handleRegenerateSecret = async (accountId: number) => {
    if (!window.confirm('Are you sure you want to regenerate the secret? The old secret will no longer work.')) {
      return;
    }

    try {
      const response = await api.post<{ clientId: string; clientSecret: string; warning: string }>(
        `/api/service-accounts/${accountId}/regenerate-secret`
      );
      
      setNewCredentials({
        clientId: response.data.clientId,
        clientSecret: response.data.clientSecret
      });
      setShowCredentialsModal(true);
      
      toast.success('Secret regenerated successfully! 🔑');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to regenerate secret');
    }
  };

  const handleDelete = async (accountId: number) => {
    if (!window.confirm('Are you sure you want to delete this service account? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/api/service-accounts/${accountId}`);
      toast.success('Service account deleted successfully! 🗑️');
      fetchAccounts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete service account');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      maxRequestsPerWindow: 100,
      allowedEndpoints: DEFAULT_SCOPE_SELECTION
    });
    setEditingAccount(null);
    setShowModal(false);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard! 📋`);
  };

  const getStatusBadge = (account: ServiceAccount) => {
    if (!account.isActive) {
      return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Disabled</span>;
    }
    return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Active</span>;
  };

  const filteredAccounts = accounts.filter((account) => {
    const haystack = `${account.name} ${account.description || ''} ${account.clientId}`.toLowerCase();
    return haystack.includes(search.toLowerCase());
  });
  const groupedScopeOptions = SERVICE_ACCOUNT_SCOPE_OPTIONS.reduce<Record<string, ScopeOption[]>>((groups, option) => {
    groups[option.group] = [...(groups[option.group] || []), option];
    return groups;
  }, {});
  const renderEndpointChip = (pattern: string, removable = false) => {
    const [method, ...pathParts] = pattern.split(':');
    const path = pathParts.join(':');

    return (
      <span
        key={pattern}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 font-mono text-[11px] text-slate-600"
      >
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${scopeMethodStyles[method] || scopeMethodStyles['*']}`}>
          {method}
        </span>
        <span className="break-all">{path}</span>
        {removable && (
          <button
            type="button"
            onClick={() => toggleScope(pattern)}
            className="ml-1 text-slate-400 transition hover:text-rose-600"
            title="Remove scope"
          >
            ×
          </button>
        )}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHero
        badge={{ label: 'API access', tone: 'cyan' }}
        title="Service Accounts"
        description="Manage machine-to-machine credentials, rate limits, and explicit endpoint scopes for external integrations."
        stats={[
          { icon: 'fas fa-robot', label: `${accounts.length} accounts` },
          { icon: 'fas fa-check-circle', label: `${accounts.filter(a => a.isActive).length} active` },
        ]}
        actions={
          <button onClick={() => setShowModal(true)} className="rounded-2xl bg-white px-6 py-3 font-semibold text-slate-900 hover:bg-slate-100">
            Create service account
          </button>
        }
      />

      {/* Enhanced Stats Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Total accounts" value={accounts.length} hint="All service accounts" icon="fas fa-robot" accent="cyan" />
        <MetricCard label="Active accounts" value={accounts.filter(a => a.isActive).length} hint="Currently enabled" icon="fas fa-check-circle" accent="green" />
        <MetricCard label="Total requests" value={accounts.reduce((sum, a) => sum + a.totalRequests, 0).toLocaleString()} hint="All time requests" icon="fas fa-chart-line" accent="blue" />
        <MetricCard label="Default rate limit" value="100" hint="Requests per 15 minutes" icon="fas fa-tachometer-alt" accent="violet" />
      </div>

      {/* Info Box */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <i className="fas fa-info-circle text-blue-500 text-xl"></i>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">About Service Accounts</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>Service accounts allow external applications to authenticate and access the API. Each account has:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>A unique Client ID and Secret for authentication</li>
                      <li>Rate limiting (default: 100 requests per 15 minutes)</li>
                      <li><strong>Fixed token expiry: 15 minutes</strong> - developers must implement auto-refresh</li>
                      <li>Can be enabled/disabled at any time</li>
                    </ul>
                    <div className="mt-4">
                      <button 
                        onClick={() => navigate('/developer-portal')}
                        className="text-blue-600 hover:text-blue-800 font-bold flex items-center"
                      >
                        <i className="fas fa-external-link-alt mr-2"></i>
                        View Detailed Developer Documentation
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by account name, description, or client ID..."
          className="w-full rounded-2xl bg-white px-5 py-3 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
        />
      </div>

      <DataGrid
        title="API service accounts"
        description={`${filteredAccounts.length} of ${accounts.length} accounts`}
        rows={filteredAccounts}
        emptyText="No service accounts found."
        columns={[
          {
            key: 'account',
            header: 'Account',
            render: (account) => (
              <div className="flex items-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-sm">
                  <i className="fas fa-robot text-lg text-white"></i>
                </div>
                <div className="ml-4">
                  <div className="text-sm font-bold text-gray-900">{account.name}</div>
                  <div className="text-sm text-gray-500">{account.description || 'No description'}</div>
                </div>
              </div>
            ),
          },
          {
            key: 'clientId',
            header: 'Client ID',
            render: (account) => (
              <div className="flex items-center space-x-2">
                <code className="rounded-lg bg-slate-100 px-2.5 py-1.5 font-mono text-xs text-slate-800 ring-1 ring-inset ring-slate-200">
                  {account.clientId.substring(0, 20)}...
                </code>
                <button onClick={() => copyToClipboard(account.clientId, 'Client ID')} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Copy Client ID">
                  <i className="fas fa-copy"></i>
                </button>
              </div>
            ),
          },
          { key: 'status', header: 'Status', render: (account) => getStatusBadge(account) },
          { key: 'scopes', header: 'Scopes', render: (account) => `${(account.allowedEndpoints || []).length} allowed scopes` },
          { key: 'rateLimit', header: 'Rate Limit', render: (account) => `${account.maxRequestsPerWindow} / 15min` },
          { key: 'lastUsed', header: 'Last used', render: (account) => account.lastUsedAt ? new Date(account.lastUsedAt).toLocaleString() : 'Never' },
          {
            key: 'actions',
            header: 'Actions',
            render: (account) => (
              <div className="flex justify-end space-x-2">
                <button onClick={() => handleEdit(account)} className="rounded-xl p-2 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-900" title="Edit Account">
                  <i className="fas fa-edit"></i>
                </button>
                <button onClick={() => handleRegenerateSecret(account.id)} className="rounded-xl p-2 text-amber-700 hover:bg-amber-50 hover:text-amber-900" title="Regenerate Secret">
                  <i className="fas fa-key"></i>
                </button>
                <button
                  onClick={() => handleToggleActive(account.id, account.isActive)}
                  className={`rounded-xl p-2 ${account.isActive ? 'text-orange-700 hover:bg-orange-50 hover:text-orange-900' : 'text-green-700 hover:bg-green-50 hover:text-green-900'}`}
                  title={account.isActive ? 'Disable Account' : 'Enable Account'}
                >
                  <i className={`fas ${account.isActive ? 'fa-ban' : 'fa-check-circle'}`}></i>
                </button>
                <button onClick={() => handleDelete(account.id)} className="rounded-xl p-2 text-red-700 hover:bg-red-50 hover:text-red-900" title="Delete Account">
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            ),
          },
        ]}
      />

      {/* Modal for Create/Edit Service Account */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-y-auto hide-scrollbar">
            <div className="p-10 relative">
              <button
                onClick={resetForm}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="fas fa-times text-xl"></i>
              </button>

              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {editingAccount ? 'Edit Service Account' : 'Create Service Account'}
                </h3>
                <p className="text-gray-600">
                  {editingAccount 
                    ? 'Update service account settings' 
                    : 'Create a new service account for API access'
                  }
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="space-y-8">
                  <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
                    <div className="bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 px-6 py-3.5 text-white">
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/12 text-white ring-1 ring-inset ring-white/20">
                              <i className="fas fa-user-shield"></i>
                            </div>
                            <div>
                              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-100/80">Access profile</p>
                              <h4 className="mt-0.5 text-lg font-semibold">Core account details</h4>
                            </div>
                          </div>
                        </div>

                        <div className="min-w-[300px] rounded-3xl bg-white/10 px-3.5 py-2 ring-1 ring-inset ring-white/15 backdrop-blur">
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-2xl bg-cyan-300/15 text-cyan-100 ring-1 ring-inset ring-cyan-100/20">
                              <i className="fas fa-clock"></i>
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-3">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-100/80">Token Expiry</p>
                                <div className="inline-flex rounded-full bg-white px-2.5 py-0.5 font-mono text-[13px] font-semibold text-slate-900">
                                  15 minutes
                                </div>
                              </div>
                              <p className="mt-0.5 truncate text-[13px] leading-5 text-white/75">
                                Fixed by security policy. Developers should refresh tokens automatically before expiry.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-4 px-6 py-4 xl:grid-cols-[1.15fr_0.85fr]">
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Account Name <span className="text-rose-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100"
                            placeholder="e.g., Mobile App, Data Sync Service"
                            required
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Description
                          </label>
                          <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                            className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100"
                            placeholder="What is this service account used for?"
                            rows={2}
                          />
                        </div>
                      </div>

                      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-3">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Traffic controls</p>
                            <h5 className="mt-0.5 text-sm font-semibold text-slate-900">Max Requests / 15 min</h5>
                          </div>
                          <div className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-500 ring-1 ring-inset ring-slate-200">
                            Default 100
                          </div>
                        </div>

                        <div className="mt-2.5 flex items-center justify-between gap-2 rounded-2xl border border-slate-200 bg-white px-2.5 py-2">
                          <button
                            type="button"
                            onClick={() => setFormData((current) => ({
                              ...current,
                              maxRequestsPerWindow: Math.max(1, current.maxRequestsPerWindow - 10)
                            }))}
                            className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-sm text-slate-600 transition hover:bg-slate-200"
                          >
                            −
                          </button>
                          <div className="flex-1 text-center leading-none">
                            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Current limit</div>
                            <input
                              type="number"
                              value={formData.maxRequestsPerWindow}
                              onChange={(e) => setFormData({...formData, maxRequestsPerWindow: parseInt(e.target.value)})}
                              className="mt-0.5 w-full bg-transparent text-center font-mono text-xl font-semibold text-slate-900 outline-none"
                              min="1"
                              max="1000"
                              required
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => setFormData((current) => ({
                              ...current,
                              maxRequestsPerWindow: current.maxRequestsPerWindow + 10
                            }))}
                            className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-sm text-slate-600 transition hover:bg-slate-200"
                          >
                            +
                          </button>
                        </div>

                        <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">
                          <i className="fas fa-info-circle text-cyan-600"></i>
                          <span>Increase only when the integration genuinely needs more throughput.</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-6 py-3">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900">Selected scope summary</h4>
                        <p className="mt-0.5 text-[13px] text-slate-500">The backend will receive these as a proper array.</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">
                          {formData.allowedEndpoints.length} selected
                        </span>
                        <button
                          type="button"
                          onClick={() => setFormData((current) => ({ ...current, allowedEndpoints: [] }))}
                          className="rounded-lg border border-slate-300 px-3 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:border-slate-400 hover:text-slate-900"
                        >
                          Clear all
                        </button>
                      </div>
                    </div>

                    <div className="flex min-h-[56px] flex-wrap items-center gap-1.5 px-6 py-3">
                      {formData.allowedEndpoints.length > 0 ? (
                        formData.allowedEndpoints.map((endpoint) => renderEndpointChip(endpoint, true))
                      ) : (
                        <p className="text-sm italic text-slate-400">No scopes selected.</p>
                      )}
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-6 py-3.5">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900">Endpoint access</h4>
                        <p className="mt-0.5 text-[13px] text-slate-500">
                          Pick the scopes this integration needs. Each selection maps to a validated backend endpoint pattern.
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-[13px] text-slate-500">
                        <span className="h-2.5 w-2.5 rounded-full bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.45)]"></span>
                        <span>{formData.allowedEndpoints.length} selected scopes</span>
                      </div>
                    </div>

                    <div className="divide-y divide-slate-200">
                      {Object.entries(groupedScopeOptions).map(([group, options]) => (
                        <div key={group}>
                          <div className="border-b border-slate-100 bg-white px-6 py-3">
                            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{group}</div>
                            <div className="mt-0.5 text-[13px] text-slate-400">
                              Choose the access level this integration should be allowed to use.
                            </div>
                          </div>

                          <div className="overflow-x-auto">
                            <table className="min-w-full table-fixed">
                              <thead>
                                <tr className="bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                                  <th className="w-14 px-6 py-2.5">Pick</th>
                                  <th className="w-[34%] px-6 py-2.5">Endpoint</th>
                                  <th className="px-6 py-2.5">Description</th>
                                </tr>
                              </thead>
                              <tbody>
                                {options.map((option) => {
                                  const selected = formData.allowedEndpoints.includes(option.value);
                                  const [method, ...pathParts] = option.value.split(':');
                                  const path = pathParts.join(':');

                                  return (
                                    <tr
                                      key={option.value}
                                      className={`cursor-pointer border-t border-slate-100 transition ${
                                        selected ? 'bg-cyan-50/70' : 'bg-white hover:bg-slate-50'
                                      }`}
                                      onClick={() => toggleScope(option.value)}
                                    >
                                      <td className="px-6 py-3 align-top">
                                        <div
                                          className={`flex h-5 w-5 items-center justify-center rounded-md border text-[11px] ${
                                            selected
                                              ? 'border-cyan-600 bg-cyan-600 text-white'
                                              : 'border-slate-300 bg-white text-transparent'
                                          }`}
                                        >
                                          <i className="fas fa-check"></i>
                                        </div>
                                      </td>
                                      <td className="px-6 py-3 align-top">
                                        <div className="flex flex-wrap items-center gap-2">
                                          <span className={`rounded px-2 py-0.5 font-mono text-[10px] font-semibold ${scopeMethodStyles[method] || scopeMethodStyles['*']}`}>
                                            {method}
                                          </span>
                                          <span className="font-mono text-xs text-slate-600">{path}</span>
                                        </div>
                                      </td>
                                      <td className="px-6 py-3 align-top">
                                        <div className="text-[13px] font-medium text-slate-900">
                                          {option.label}
                                          {option.recommended && (
                                            <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                              Recommended
                                            </span>
                                          )}
                                        </div>
                                        <p className="mt-0.5 text-[13px] leading-5 text-slate-500">{option.description}</p>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-200 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg hover:from-cyan-700 hover:to-blue-700 transition-all duration-200 font-medium shadow-lg"
                  >
                    <i className={`fas ${editingAccount ? 'fa-save' : 'fa-plus'} mr-2`}></i>
                    {editingAccount ? 'Update' : 'Create'} Account
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Credentials Modal */}
      {showCredentialsModal && newCredentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
            <div className="p-8">
              <div className="mb-6">
                <div className="flex items-center justify-center mb-4">
                  <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="fas fa-check text-green-600 text-3xl"></i>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 text-center mb-2">
                  Service Account Created!
                </h3>
                <p className="text-red-600 text-center font-medium">
                  ⚠️ Save these credentials securely. The secret will not be shown again!
                </p>
              </div>

              <div className="space-y-4 bg-gray-50 p-6 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client ID
                  </label>
                  <div className="flex items-center space-x-2">
                    <code className="flex-1 bg-white px-4 py-3 rounded-lg border border-gray-300 text-sm font-mono break-all">
                      {newCredentials.clientId}
                    </code>
                    <button
                      onClick={() => copyToClipboard(newCredentials.clientId, 'Client ID')}
                      className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      title="Copy"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client Secret
                  </label>
                  <div className="flex items-center space-x-2">
                    <code className="flex-1 bg-white px-4 py-3 rounded-lg border border-gray-300 text-sm font-mono break-all">
                      {newCredentials.clientSecret}
                    </code>
                    <button
                      onClick={() => copyToClipboard(newCredentials.clientSecret, 'Client Secret')}
                      className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      title="Copy"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <i className="fas fa-info-circle text-blue-500"></i>
                  </div>
                  <div className="ml-3 text-sm text-blue-700">
                    <p className="font-medium mb-2">How to use these credentials:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Send a POST request to <code>/api/service-accounts/authenticate</code></li>
                      <li>Include the clientId and clientSecret in the request body</li>
                      <li>You'll receive an access token valid for <strong>15 minutes</strong></li>
                      <li>Use the token in the Authorization header: <code>Bearer YOUR_TOKEN</code></li>
                      <li><strong>Important:</strong> Implement automatic token refresh in your script every 15 minutes</li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-8">
                <button
                  onClick={() => {
                    setShowCredentialsModal(false);
                    setNewCredentials(null);
                  }}
                  className="px-8 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg hover:from-cyan-700 hover:to-blue-700 transition-all duration-200 font-medium shadow-lg"
                >
                  I've Saved the Credentials
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceAccounts;
